import { ConfigProvider, FloatButton } from "antd";
import { ServerIcon, type LucideIcon } from "lucide-react";
import { createContext, useState } from "react";
import { useTranslation } from "react-i18next";

import Modal from "./Modal"

export interface ToolkitProps {
  children?: React.ReactNode;
}

export interface ToolkitContextValue {
  showModal: (
    title: string,
    content: React.ReactNode,
    options?: ToolboxModalProps["options"]
  ) => void;
}

export interface IToolkitContext extends React.FC<ToolkitProps> {
  context: React.Context<ToolkitContextValue>;
}

export type ToolboxModalProps = {
  open: boolean;
  title: string;
  content?: React.ReactNode;
  options?: {
    showFooter?: boolean;
    onOk?: () => void;
    width?: number | string;
    cancelText?: string;
    okText?: string;
  };
};

export type ToolkitShortcutProps = {
  key: string;
  icon: LucideIcon;
  onClick: () => void;
};

export const ToolkitContext = createContext<ToolkitContextValue>({} as ToolkitContextValue);
export const Toolkit: IToolkitContext = ({ children }) => {
  const { t } = useTranslation();

  const [toolkitShortcuts, setToolkitShortcuts] = useState<
    ToolkitShortcutProps[]
  >([]);

  const [modal, setModal] = useState<ToolboxModalProps>({
    open: false,
    title: "",
    content: "",
  });

  const setModalShowState = (isOpen: boolean) => {
    setModal((prev) => ({ ...prev, open: isOpen }));
  };

  const showModal = (
    title: string,
    content: React.ReactNode,
    options?: ToolboxModalProps["options"]
  ) => {
    setModal({ open: true, title, content, options });
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#5ddea6",
        },
      }}
    >
      <ToolkitContext.Provider
        value={{
          showModal,
        }}
      >
        {children}
        {toolkitShortcuts.length > 0 && (
          <FloatButton.Group
            trigger="click"
            type="primary"
            style={{ insetInlineEnd: 24 }}
            icon={<ServerIcon size={16} />}
          >
            {toolkitShortcuts.map((shortcut) => (
              <FloatButton
                key={shortcut.key}
                icon={<shortcut.icon size={16} />}
                onClick={shortcut.onClick}
              />
            ))}
          </FloatButton.Group>
        )}
      </ToolkitContext.Provider>
      <Modal
        open={modal.open}
        setOpen={setModalShowState}
        title={modal.title}
        content={modal.content}
        showFooter={modal.options?.showFooter}
        onOk={modal.options?.onOk}
        width={modal.options?.width}
        cancelText={modal.options?.cancelText || t("cancel")}
        okText={modal.options?.okText || t("ok")}
      />
    </ConfigProvider>
  );
};

Toolkit.context = ToolkitContext;
